import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelpers } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  UserCircleIcon,
  EnvelopeIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { Dialog, Switch } from '@headlessui/react';

const roleLabels = {
  SUPER_ADMIN: 'Super Admin',
  MANAGING_ADMIN: 'Managing Admin',
  ADMIN: 'Admin',
  DATA_ANALYST: 'Data Analyst',
  VIEWER: 'Viewer'
};

const roleBadgeColors = {
  SUPER_ADMIN: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  MANAGING_ADMIN: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  ADMIN: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  DATA_ANALYST: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  VIEWER: 'bg-surface-100 text-surface-800 dark:bg-surface-800 dark:text-surface-200'
};

// Helper to format time remaining
function formatTimeRemaining(nextAvailableAt) {
  const now = new Date();
  const next = new Date(nextAvailableAt);
  const diffMs = next - now;
  
  if (diffMs <= 0) return null;
  
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

export default function UserManagement() {
  const { user: currentUser, hasPermission, getCurrentCompanyId, isSuperUser } = useAuthStore();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [resendingUserId, setResendingUserId] = useState(null);
  const [permissionsUser, setPermissionsUser] = useState(null);
  
  // Use getCurrentCompanyId to respect selected company for super/managing admins
  const currentCompanyId = getCurrentCompanyId();
  
  // Super users without a selected company can see all users
  // Everyone else (including super users with a selected company) sees company-specific users
  const { data, isLoading } = useQuery({
    queryKey: ['users', currentCompanyId],
    queryFn: () => {
      if (currentCompanyId) {
        // Use company-specific endpoint (works for all roles with MANAGE_COMPANY_USERS)
        return apiHelpers.getUsersByCompany(currentCompanyId);
      } else if (isSuperUser()) {
        // Only super users can get all users without a company filter
        return apiHelpers.getUsers();
      }
      // Fallback - should not happen as regular users always have a companyId
      return apiHelpers.getUsers();
    }
  });
  
  const createMutation = useMutation({
    mutationFn: (userData) => apiHelpers.createUser(userData),
    onSuccess: () => {
      toast.success('Invitation sent!');
      queryClient.invalidateQueries(['users']);
      setShowAddModal(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to invite user');
    }
  });
  
  const deleteMutation = useMutation({
    mutationFn: (id) => apiHelpers.deleteUser(id),
    onSuccess: () => {
      toast.success('User deleted');
      queryClient.invalidateQueries(['users']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete user');
    }
  });
  
  const resendMutation = useMutation({
    mutationFn: (id) => {
      setResendingUserId(id);
      return apiHelpers.resendVerificationEmail(id);
    },
    onSuccess: (_, userId) => {
      const user = users.find(u => u._id === userId);
      toast.success(`Verification email resent to ${user?.email || 'user'}`);
      setResendingUserId(null);
    },
    onError: (error) => {
      const errorData = error.response?.data;
      if (error.response?.status === 429 && errorData?.nextAvailableAt) {
        const timeRemaining = formatTimeRemaining(errorData.nextAvailableAt);
        toast.error(`Rate limited. Try again in ${timeRemaining || 'later'}.`);
      } else {
        toast.error(errorData?.error || 'Failed to resend verification email');
      }
      setResendingUserId(null);
    }
  });
  
  const permissionsMutation = useMutation({
    mutationFn: ({ id, data }) => apiHelpers.updateUserPermissions(id, data),
    onSuccess: () => {
      toast.success('Permissions updated');
      queryClient.invalidateQueries(['users']);
      setPermissionsUser(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update permissions');
    }
  });
  
  // Check if current user can manage viewer permissions
  const canManageViewerPermissions = ['SUPER_ADMIN', 'MANAGING_ADMIN', 'ADMIN'].includes(currentUser?.role);
  
  const users = data?.data?.data || [];
  const filteredUsers = users.filter(u =>
    u.email.toLowerCase().includes(search.toLowerCase()) ||
    u.firstName?.toLowerCase().includes(search.toLowerCase()) ||
    u.lastName?.toLowerCase().includes(search.toLowerCase())
  );
  
  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
            User Management
          </h1>
          <p className="text-surface-500 dark:text-surface-400 mt-1">
            Manage team members and their access
          </p>
        </div>
        {hasPermission('manage_company_users') && (
          <button onClick={() => setShowAddModal(true)} className="btn-primary">
            <PlusIcon className="w-5 h-5 mr-2" />
            Invite User
          </button>
        )}
      </div>
      
      {/* Search */}
      <div className="relative mb-6">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search users..."
          className="input pl-10 max-w-md"
        />
      </div>
      
      {/* Users table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="flex items-center gap-4">
                <div className="skeleton w-10 h-10 rounded-full" />
                <div className="flex-1">
                  <div className="skeleton h-4 w-48 mb-2" />
                  <div className="skeleton h-3 w-32" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-12 text-center">
            <UserCircleIcon className="w-12 h-12 mx-auto text-surface-400 mb-4" />
            <h3 className="font-medium text-surface-900 dark:text-white mb-1">
              {search ? 'No users found' : 'No users yet'}
            </h3>
            <p className="text-surface-500 text-sm">
              {search ? 'Try a different search term' : 'Invite your first team member'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-surface-50 dark:bg-surface-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-surface-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-200 dark:divide-surface-800">
              {filteredUsers.map(user => (
                <tr key={user._id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white font-medium">
                        {user.firstName?.[0]}{user.lastName?.[0]}
                      </div>
                      <div>
                        <div className="font-medium text-surface-900 dark:text-white">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-sm text-surface-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`badge ${roleBadgeColors[user.role]}`}>
                      {roleLabels[user.role]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {!user.isEmailVerified ? (
                      <span className="badge-warning">Pending</span>
                    ) : user.isActive ? (
                      <span className="badge-success">Active</span>
                    ) : (
                      <span className="badge-neutral">Inactive</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-surface-500">
                    {user.lastLogin 
                      ? new Date(user.lastLogin).toLocaleDateString()
                      : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      {!user.isEmailVerified && (
                        <button
                          onClick={() => resendMutation.mutate(user._id)}
                          disabled={resendMutation.isPending && resendingUserId === user._id}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/50 hover:bg-amber-100 dark:hover:bg-amber-900/50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Resend verification email"
                        >
                          <ArrowPathIcon className={`w-4 h-4 ${resendMutation.isPending && resendingUserId === user._id ? 'animate-spin' : ''}`} />
                          Resend
                        </button>
                      )}
                      {user.role === 'VIEWER' && canManageViewerPermissions && (
                        <button
                          onClick={() => setPermissionsUser(user)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-950/50 hover:bg-primary-100 dark:hover:bg-primary-900/50 rounded-md transition-colors"
                          title="Edit permissions"
                        >
                          <Cog6ToothIcon className="w-4 h-4" />
                          Permissions
                        </button>
                      )}
                      {user._id !== currentUser?._id && (
                        <button
                          onClick={() => {
                            if (window.confirm('Are you sure you want to delete this user?')) {
                              deleteMutation.mutate(user._id);
                            }
                          }}
                          className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-950 rounded-md transition-colors"
                          title="Delete user"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      
      {/* Add User Modal */}
      <AddUserModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={(data) => createMutation.mutate(data)}
        isLoading={createMutation.isPending}
        companyId={currentCompanyId}
      />
      
      {/* Viewer Permissions Modal */}
      {permissionsUser && (
        <ViewerPermissionsModal
          user={permissionsUser}
          onClose={() => setPermissionsUser(null)}
          onSubmit={(data) => permissionsMutation.mutate({ id: permissionsUser._id, data })}
          isLoading={permissionsMutation.isPending}
        />
      )}
    </div>
  );
}

function AddUserModal({ isOpen, onClose, onSubmit, isLoading, companyId }) {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'VIEWER'
  });
  
  // Viewer-specific permissions
  const [allDashboardsAccess, setAllDashboardsAccess] = useState(true);
  const [selectedDashboards, setSelectedDashboards] = useState([]);
  const [hasAIAccess, setHasAIAccess] = useState(false);
  
  // Fetch available dashboards for the company
  const { data: dashboardsData, isLoading: loadingDashboards } = useQuery({
    queryKey: ['dashboards', companyId],
    queryFn: () => apiHelpers.getDashboards({ companyId }),
    enabled: !!companyId && formData.role === 'VIEWER'
  });
  
  const dashboards = dashboardsData?.data?.data || dashboardsData?.data || [];
  
  const handleDashboardToggle = (dashboardId) => {
    setSelectedDashboards(prev => 
      prev.includes(dashboardId)
        ? prev.filter(id => id !== dashboardId)
        : [...prev, dashboardId]
    );
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    const userData = {
      ...formData,
      companyId: companyId
    };
    
    // Add viewer-specific permissions if role is VIEWER
    if (formData.role === 'VIEWER') {
      userData.dashboardAccess = allDashboardsAccess ? [] : selectedDashboards;
      userData.customPermissions = {
        granted: hasAIAccess ? ['use_ai'] : [],
        revoked: hasAIAccess ? [] : ['use_ai']
      };
    }
    
    onSubmit(userData);
  };
  
  const availableRoles = ['ADMIN', 'DATA_ANALYST', 'VIEWER'];
  const isViewerRole = formData.role === 'VIEWER';
  
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-lg bg-white dark:bg-surface-900 rounded-2xl shadow-xl max-h-[90vh] flex flex-col">
          <Dialog.Title className="text-lg font-display font-semibold p-6 border-b border-surface-200 dark:border-surface-800 text-surface-900 dark:text-white">
            Invite User
          </Dialog.Title>
          
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">First Name</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    required
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">Last Name</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    required
                    className="input"
                  />
                </div>
              </div>
              
              <div>
                <label className="label">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="input"
                />
              </div>
              
              <div>
                <label className="label">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="input"
                >
                  {availableRoles.map(role => (
                    <option key={role} value={role}>{roleLabels[role]}</option>
                  ))}
                </select>
              </div>
              
              {/* Viewer-specific permissions */}
              {isViewerRole && (
                <div className="space-y-4 pt-4 border-t border-surface-200 dark:border-surface-700">
                  <h3 className="text-sm font-medium text-surface-900 dark:text-white">
                    Viewer Permissions
                  </h3>
                  
                  {/* AI Assistant Access */}
                  <div className="flex items-center justify-between p-4 bg-surface-50 dark:bg-surface-800/50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                        <ChatBubbleLeftRightIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="font-medium text-surface-900 dark:text-white">AI Assistant</div>
                        <div className="text-sm text-surface-500">Allow access to AI chat</div>
                      </div>
                    </div>
                    <Switch
                      checked={hasAIAccess}
                      onChange={setHasAIAccess}
                      className={`${
                        hasAIAccess ? 'bg-primary-600' : 'bg-surface-300 dark:bg-surface-600'
                      } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-surface-900`}
                    >
                      <span
                        className={`${
                          hasAIAccess ? 'translate-x-6' : 'translate-x-1'
                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                      />
                    </Switch>
                  </div>
                  
                  {/* Dashboard Access */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <ChartBarIcon className="w-5 h-5 text-surface-500" />
                      <h4 className="font-medium text-surface-900 dark:text-white">Dashboard Access</h4>
                    </div>
                    
                    {/* All dashboards toggle */}
                    <div className="flex items-center justify-between p-3 mb-3 bg-surface-50 dark:bg-surface-800/50 rounded-lg">
                      <span className="text-sm font-medium text-surface-700 dark:text-surface-300">
                        Access to all dashboards
                      </span>
                      <Switch
                        checked={allDashboardsAccess}
                        onChange={setAllDashboardsAccess}
                        className={`${
                          allDashboardsAccess ? 'bg-primary-600' : 'bg-surface-300 dark:bg-surface-600'
                        } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-surface-900`}
                      >
                        <span
                          className={`${
                            allDashboardsAccess ? 'translate-x-6' : 'translate-x-1'
                          } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                        />
                      </Switch>
                    </div>
                    
                    {/* Individual dashboard selection */}
                    {!allDashboardsAccess && (
                      <div className="space-y-2 max-h-48 overflow-y-auto border border-surface-200 dark:border-surface-700 rounded-lg p-3">
                        {loadingDashboards ? (
                          <div className="flex items-center justify-center py-4">
                            <div className="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                          </div>
                        ) : dashboards.length === 0 ? (
                          <p className="text-sm text-surface-500 text-center py-4">
                            No dashboards available
                          </p>
                        ) : (
                          dashboards.map(dashboard => (
                            <label
                              key={dashboard._id}
                              className="flex items-center gap-3 p-2 rounded-lg hover:bg-surface-50 dark:hover:bg-surface-800/50 cursor-pointer transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={selectedDashboards.includes(dashboard._id)}
                                onChange={() => handleDashboardToggle(dashboard._id)}
                                className="w-4 h-4 rounded border-surface-300 text-primary-600 focus:ring-primary-500 dark:border-surface-600 dark:bg-surface-800"
                              />
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center flex-shrink-0">
                                  <ChartBarIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                                </div>
                                <span className="text-sm font-medium text-surface-900 dark:text-white truncate">
                                  {dashboard.name}
                                </span>
                              </div>
                            </label>
                          ))
                        )}
                      </div>
                    )}
                    
                    {!allDashboardsAccess && selectedDashboards.length === 0 && (
                      <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
                        ⚠️ No dashboards selected. User won't be able to view any dashboards.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3 p-6 border-t border-surface-200 dark:border-surface-800">
              <button type="button" onClick={onClose} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" disabled={isLoading} className="btn-primary">
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                ) : (
                  <EnvelopeIcon className="w-5 h-5 mr-2" />
                )}
                Send Invitation
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

function ViewerPermissionsModal({ user, onClose, onSubmit, isLoading }) {
  // Use the Viewer's company ID to fetch dashboards, not the admin's
  const viewerCompanyId = typeof user.companyId === 'object' ? user.companyId._id : user.companyId;
  
  // Fetch available dashboards for the Viewer's company
  const { data: dashboardsData, isLoading: loadingDashboards } = useQuery({
    queryKey: ['dashboards', viewerCompanyId],
    queryFn: () => apiHelpers.getDashboards({ companyId: viewerCompanyId }),
    enabled: !!viewerCompanyId
  });
  
  const dashboards = dashboardsData?.data?.data || dashboardsData?.data || [];
  
  // Initialize state from user's current permissions
  const [selectedDashboards, setSelectedDashboards] = useState([]);
  const [hasAIAccess, setHasAIAccess] = useState(false);
  const [allDashboardsAccess, setAllDashboardsAccess] = useState(true);
  
  useEffect(() => {
    if (user) {
      // Dashboard access - empty array means all dashboards
      const userDashboards = user.dashboardAccess || [];
      setAllDashboardsAccess(userDashboards.length === 0);
      setSelectedDashboards(userDashboards.map(d => typeof d === 'string' ? d : d._id));
      
      // AI access - check if use_ai is in granted permissions
      const hasAI = user.customPermissions?.granted?.includes('use_ai') || false;
      setHasAIAccess(hasAI);
    }
  }, [user]);
  
  const handleDashboardToggle = (dashboardId) => {
    setSelectedDashboards(prev => 
      prev.includes(dashboardId)
        ? prev.filter(id => id !== dashboardId)
        : [...prev, dashboardId]
    );
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Build custom permissions
    const customPermissions = {
      granted: hasAIAccess ? ['use_ai'] : [],
      revoked: hasAIAccess ? [] : ['use_ai']
    };
    
    // Dashboard access - empty array means all dashboards
    const dashboardAccess = allDashboardsAccess ? [] : selectedDashboards;
    
    onSubmit({
      dashboardAccess,
      customPermissions
    });
  };
  
  return (
    <Dialog open={true} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-lg bg-white dark:bg-surface-900 rounded-2xl shadow-xl max-h-[85vh] flex flex-col">
          <Dialog.Title className="text-lg font-display font-semibold p-6 border-b border-surface-200 dark:border-surface-800 text-surface-900 dark:text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white font-medium text-sm">
                {user.firstName?.[0]}{user.lastName?.[0]}
              </div>
              <div>
                <div>Permissions for {user.firstName} {user.lastName}</div>
                <div className="text-sm font-normal text-surface-500">{user.email}</div>
              </div>
            </div>
          </Dialog.Title>
          
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              {/* AI Assistant Access */}
              <div className="flex items-center justify-between p-4 bg-surface-50 dark:bg-surface-800/50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                    <ChatBubbleLeftRightIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-medium text-surface-900 dark:text-white">AI Assistant</div>
                    <div className="text-sm text-surface-500">Allow access to the AI chat feature</div>
                  </div>
                </div>
                <Switch
                  checked={hasAIAccess}
                  onChange={setHasAIAccess}
                  className={`${
                    hasAIAccess ? 'bg-primary-600' : 'bg-surface-300 dark:bg-surface-600'
                  } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-surface-900`}
                >
                  <span
                    className={`${
                      hasAIAccess ? 'translate-x-6' : 'translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </Switch>
              </div>
              
              {/* Dashboard Access */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <ChartBarIcon className="w-5 h-5 text-surface-500" />
                  <h3 className="font-medium text-surface-900 dark:text-white">Dashboard Access</h3>
                </div>
                
                {/* All dashboards toggle */}
                <div className="flex items-center justify-between p-3 mb-3 bg-surface-50 dark:bg-surface-800/50 rounded-lg">
                  <span className="text-sm font-medium text-surface-700 dark:text-surface-300">
                    Access to all dashboards
                  </span>
                  <Switch
                    checked={allDashboardsAccess}
                    onChange={setAllDashboardsAccess}
                    className={`${
                      allDashboardsAccess ? 'bg-primary-600' : 'bg-surface-300 dark:bg-surface-600'
                    } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-surface-900`}
                  >
                    <span
                      className={`${
                        allDashboardsAccess ? 'translate-x-6' : 'translate-x-1'
                      } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                    />
                  </Switch>
                </div>
                
                {/* Individual dashboard selection */}
                {!allDashboardsAccess && (
                  <div className="space-y-2 max-h-48 overflow-y-auto border border-surface-200 dark:border-surface-700 rounded-lg p-3">
                    {loadingDashboards ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : dashboards.length === 0 ? (
                      <p className="text-sm text-surface-500 text-center py-4">
                        No dashboards available
                      </p>
                    ) : (
                      dashboards.map(dashboard => (
                        <label
                          key={dashboard._id}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-surface-50 dark:hover:bg-surface-800/50 cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedDashboards.includes(dashboard._id)}
                            onChange={() => handleDashboardToggle(dashboard._id)}
                            className="w-4 h-4 rounded border-surface-300 text-primary-600 focus:ring-primary-500 dark:border-surface-600 dark:bg-surface-800"
                          />
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center flex-shrink-0">
                              <ChartBarIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                            </div>
                            <span className="text-sm font-medium text-surface-900 dark:text-white truncate">
                              {dashboard.name}
                            </span>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                )}
                
                {!allDashboardsAccess && selectedDashboards.length === 0 && (
                  <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
                    ⚠️ No dashboards selected. User won't be able to view any dashboards.
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex justify-end gap-3 p-6 border-t border-surface-200 dark:border-surface-800">
              <button type="button" onClick={onClose} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" disabled={isLoading} className="btn-primary">
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                ) : (
                  <Cog6ToothIcon className="w-5 h-5 mr-2" />
                )}
                Save Permissions
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

